import React, { useMemo, useState, useEffect } from "react";
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Select,
  MenuItem,
  TextField,
  CircularProgress
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import BarChart from "./Charts/BarChart";
import config from "../../../config";

const classifyStatus = (latency) => {
  if (latency == null) return { label: "Unknown", style: { bg: "#E5E7EB", border: "#9CA3AF", text: "#374151" } };
  if (latency > 3000) return { label: "Critical", style: { bg: "#FFE6E6", border: "#EF4444", text: "#B91C1C" } };
  if (latency > 2000) return { label: "Slow", style: { bg: "#FFF4CC", border: "#F59E0B", text: "#B45309" } };
  return { label: "Healthy", style: { bg: "#E8F9F0", border: "#10B981", text: "#065F46" } };
};

const TrainingPerformanceDetailsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const selectedDateRange = location.state?.selectedDateRange;
  const [latencyData, setLatencyData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchLatencyData();
  }, [selectedDateRange]);

  const fetchLatencyData = async () => {
    try {
      setLoading(true);
      const baseUrl = config.API_BASE_URL_V1;
      let url = `${baseUrl}/analytics/training-performance/api-latency`;

      // Add date filters if available
      if (selectedDateRange?.start_date && selectedDateRange?.end_date) {
        url += `?start_date=${selectedDateRange.start_date}&end_date=${selectedDateRange.end_date}`;
      }

      const response = await fetch(url, { headers: config.getHeaders() });
      const result = await response.json();
      setLatencyData(result.data || []);
      setError(null);
    } catch (err) {
      setError(err.message);
      console.error("Error fetching latency data:", err);
    } finally {
      setLoading(false);
    }
  };

  const baseRows = useMemo(() => {
    return latencyData.map((entry) => {
      const clientName = entry.organization_name || "Unknown Client";
      const avgLatencyMs = typeof entry.avg_latency_ms === "number" ? entry.avg_latency_ms : null;
      const eventCount = entry.event_count || 0;
      const status = classifyStatus(avgLatencyMs);
      return { clientName, avgLatencyMs, eventCount, status };
    });
  }, [latencyData]);

  // ⭐ Missing states added
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [sortDirection, setSortDirection] = useState("asc");
  const [page, setPage] = useState(1);
  const rowsPerPage = 5;

  // ⭐ Apply filters
  const filteredRows = useMemo(() => {
    let rows = [...baseRows];

    if (search) {
      rows = rows.filter((r) =>
        r.clientName.toLowerCase().includes(search.toLowerCase())
      );
    }

    if (statusFilter !== "All") {
      rows = rows.filter((r) => r.status.label === statusFilter);
    }

    rows.sort((a, b) =>
      sortDirection === "asc" ? a.avgLatencyMs - b.avgLatencyMs : b.avgLatencyMs - a.avgLatencyMs
    );

    return rows;
  }, [baseRows, search, statusFilter, sortDirection]);

  const totalPages = Math.ceil(filteredRows.length / rowsPerPage);

  const paginatedRows = filteredRows.slice((page - 1) * rowsPerPage, page * rowsPerPage);

  const chartLabels = filteredRows.map((r) => r.clientName);
  const chartData = filteredRows.map((r) => r.avgLatencyMs);

  const exportCSV = () => {
    const header = "Client,Latency (ms),Event Count,Status\n";
    const rows = filteredRows
      .map((r) => `${r.clientName},${r.avgLatencyMs},${r.eventCount},${r.status.label}`)
      .join("\n");

    const blob = new Blob([header + rows], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "latency-report.csv";
    a.click();
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography sx={{ color: "red" }}>
          Error loading latency data: {error}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Button
          variant="outlined"
          onClick={() => navigate(-1)}
          sx={{
            mb: 2,
            px: 2.5,
            py: 1,
            fontWeight: 600,
            borderRadius: "8px",
            textTransform: "none",
            borderColor: "#6A5BFF",
            color: "#6A5BFF",
            "&:hover": { background: "#6A5BFF", color: "#fff" },
          }}
        >
          Back
        </Button>
      </Box>

      <Typography variant="h6" sx={{ fontWeight: 600 }}>
        API Response Latency Details
      </Typography>

      <Box sx={{ p: 3, border: "1px solid #E5E7EB", mb: 3 }}>
        <BarChart labels={chartLabels} data={chartData} />
      </Box>

      <Box sx={{ p: 3, border: "1px solid #E5E7EB" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
          <TextField
            label="Search Client"
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <Select value={statusFilter} size="small" onChange={(e) => setStatusFilter(e.target.value)}>
            <MenuItem value="All">All</MenuItem>
            <MenuItem value="Healthy">Healthy</MenuItem>
            <MenuItem value="Slow">Slow</MenuItem>
            <MenuItem value="Critical">Critical</MenuItem>
          </Select>

          <Button onClick={exportCSV} size="small" variant="outlined">
            Export CSV
          </Button>
        </Box>

        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: "#F3F4F6" }}>
              <TableCell sx={{ fontWeight: 600 }}>Client</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Avg Latency (ms)</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Event Count</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedRows.map((row) => (
              <TableRow key={row.clientName}>
                <TableCell>{row.clientName}</TableCell>
                <TableCell>{row.avgLatencyMs}</TableCell>
                <TableCell>{row.eventCount}</TableCell>
                <TableCell>
                  <Box
                    sx={{
                      display: "inline-block",
                      px: 2,
                      py: 0.5,
                      borderRadius: "6px",
                      backgroundColor: row.status.style.bg,
                      border: `1px solid ${row.status.style.border}`,
                      color: row.status.style.text,
                      fontWeight: 500,
                      fontSize: "0.85rem"
                    }}
                  >
                    {row.status.label}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {/* Pagination */}
        <Box sx={{ display: "flex", justifyContent: "center", mt: 2, gap: 2 }}>
          <Button disabled={page === 1} onClick={() => setPage(page - 1)}>⬅ Prev</Button>
          <Typography>Page {page} / {totalPages}</Typography>
          <Button disabled={page === totalPages} onClick={() => setPage(page + 1)}>Next ➡</Button>
        </Box>
      </Box>
    </Box>
  );
};

export default TrainingPerformanceDetailsPage;
